import { AutocompleteField } from "@/features/FilterTable/AutocompleteField";
import {
  dateSchema,
  formatDateValueToISOString,
  parseToDateValue,
} from "@/features/FormCard/utils";
import { getAuthorized, postClientAction } from "@/lib/fetch";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { useCurrentPageFromUrl } from "@/hooks/useClientActions";

const PROTECTED_DATES = [
  "2025-12-31",
  "2025-06-30",
  "2024-06-30",
  "2023-12-31",
];

const RunCalcSchema = z.object({
  reportingDate: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional()
    .nullable()
    .describe("date")
    .refine(
      (date) => {
        if (!date) return true;
        const dateStr = formatDateValueToISOString(date);
        return !PROTECTED_DATES.includes(dateStr as string);
      },
      {
        message: `La date ${PROTECTED_DATES.join(
          ", ",
        )} est protégée et ne peut pas être sélectionnée.`,
      },
    ),
});

export type RunCalcSchemaType = z.infer<typeof RunCalcSchema>;

const A7OPCVMRunCalcPopover = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { isValid },
    reset,
  } = useForm<RunCalcSchemaType>({
    mode: "onChange",
    defaultValues: {
      reportingDate: undefined,
    },
    resolver: zodResolver(RunCalcSchema) as Resolver<RunCalcSchemaType>,
  });

  const table_name = "a7_opcvm_client_portfolios";

  const { data: dateOptions } = useQuery({
    queryKey: ["a7", table_name, "dates", "with_protected_date"],
    queryFn: async () => {
      return getAuthorized(
        `${import.meta.env.VITE_BACKEND_URL}/a7/${table_name}/dates`,
      )
        .then((response) => response.json())
        .then((res) => {
          return res
            .filter((date: string) => date === "2026-06-30")
            .map((date: string) => ({
              label: date,
              value: date,
            }));
        });
    },
  });

  const currentPage = useCurrentPageFromUrl();

  const runCalcMutation = useMutation({
    mutationFn: async (data: RunCalcSchemaType) => {
      const formattedDate = formatDateValueToISOString(data.reportingDate);

      return await postClientAction(
        `${import.meta.env.VITE_BACKEND_URL}/a7/a7_opcvm_run_calc`,
        {
          reportingDate: formattedDate ?? "",
          prefix: "a7_opcvm",
          client: "a7_opcvm",
          action_button_name: t("recalculate.action"),
          action_execution_page: currentPage ?? "",
        },
      );
    },
    onSuccess: () => {
      toast.success(t("calculations.started"));
      setIsOpen(false);
      reset();
    },
    onError: () => {
      toast.error(t("calculations.error"));
    },
  });

  const onSubmit = (data: RunCalcSchemaType) => {
    runCalcMutation.mutate(data);
  };

  return (
    <Popover
      placement="bottom"
      showArrow
      offset={10}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <PopoverTrigger>
        <Button
          size="sm"
          radius="full"
          style={{
            border: "solid 2px transparent",
            backgroundImage:
              "linear-gradient(hsl(var(--heroui-background)), hsl(var(--heroui-background))), linear-gradient(83.87deg, #F54180, #9353D3)",
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
          }}
          endContent={<ChevronDownIcon className="h-4 w-4" />}
        >
          {t("recalculate.action")}
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-1">
          <div>
            {dateOptions && (
              <AutocompleteField
                field={{
                  name: "reportingDate",
                  label: t("templates.reporting_date", {
                    defaultValue: "Date",
                  }),
                  type: "autocomplete",
                  options: dateOptions,
                }}
                setValue={setValue as any}
                watch={watch}
              />
            )}
          </div>

          <footer className="mt-4 w-full">
            <Button
              color="primary"
              radius="full"
              size="sm"
              type="submit"
              className="w-24"
              isDisabled={!isValid}
              isLoading={runCalcMutation.isPending}
            >
              {t("actions.calculate")}
            </Button>
          </footer>
        </form>
      </PopoverContent>
    </Popover>
  );
};

export default A7OPCVMRunCalcPopover;
